"use client";

import * as React from "react";
import { Copy, Radio, Wrench, WandSparkles as Wand2, Mail, Upload } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactCards = [];
function IntakeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [agreed, setAgreed] = React.useState(false);
  const [fileName, setFileName] = React.useState<string>("");
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-white/60 p-8 rounded-md">
        <p className="font-default text-xs uppercase tracking-widest text-primary mb-3">
          // RESERVATION RECEIVED
        </p>
        <p className="text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} className="btn-secondary">
          [ NEW BOOKING ]
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      {}
      <div className="space-y-5">
        <p className="font-default text-xs uppercase tracking-widest text-primary">
          // 01 — PASSENGER INFO
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Min 2 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                  Full Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="323-492-6292" className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-5">
        <p className="font-default text-xs uppercase tracking-widest text-primary">
          // 02 — TRIP DETAILS
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField control={control} name="service_type" rules={{
          required: "Service type is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                  Service Type
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Airport Transfer">Airport Transfer</SelectItem>
                    <SelectItem value="VIP Transportation">VIP Transportation</SelectItem>
                    <SelectItem value="Hourly Booking">Hourly Booking</SelectItem>
                    <SelectItem value="Event Service">Event Service</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="preferred_contact_date" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                  Travel Date
                </FormLabel>
                <FormControl>
                  <Input type="date" className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-5">
        <p className="font-default text-xs uppercase tracking-widest text-primary">
          // 03 — ADDITIONAL INFO
        </p>
        <FormField control={control} name="mission_details" rules={{
        required: "Trip details are required",
        minLength: {
          value: 10,
          message: "Min 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                Trip Requirements
              </FormLabel>
              <FormControl>
                <Textarea rows={5} placeholder="// Pickup location, destination, number of passengers..." className="border border-border bg-transparent font-default text-sm focus-visible:ring-0 focus-visible:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <div>
          <Label className="font-default text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">
            Itinerary Attachment
          </Label>
          <label htmlFor="damage-files" className="flex flex-col items-center justify-center border border-dashed border-border bg-muted/30 px-6 py-10 cursor-pointer hover:border-primary transition-colors rounded-lg">
            <Upload className="size-6 text-muted-foreground mb-3" />
            <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              {fileName || "DRAG & DROP // OR BROWSE FILES"}
            </span>
            <input id="damage-files" type="file" className="hidden" onChange={e => setFileName(e.target.files?.[0]?.name || "")} />
          </label>
        </div>

        {}
        <div className="space-y-3">
          <Label className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
            Vehicle Preference
          </Label>
          <RadioGroup defaultValue="standard" className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["Sedan", "SUV", "Luxury"].map((tier, i) => <label key={tier} data-index={i} htmlFor={`tier-${tier}`} className="flex items-center gap-3 border border-border px-4 py-3 cursor-pointer hover:border-primary transition-colors rounded-lg">
                <RadioGroupItem value={tier} id={`tier-${tier}`} />
                <span className="font-default text-xs uppercase tracking-widest text-foreground">
                  {tier}
                </span>
              </label>)}
          </RadioGroup>
        </div>
      </div>

      {}
      <div className="flex items-start gap-3 border-t border-border pt-6">
        <Checkbox id="agree" checked={agreed} onCheckedChange={v => setAgreed(Boolean(v))} className="mt-0.5" />
        <Label htmlFor="agree" className="font-default text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">
          I agree to the booking terms and privacy policy
        </Label>
      </div>

      {submitError && <div className="border border-destructive/50 bg-destructive/10 p-4 rounded-lg">
          <p className="font-default text-xs text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting || !agreed} className="btn-primary w-full">
        {isSubmitting ? "[ PROCESSING... ]" : "[ SUBMIT RESERVATION → ]"}
      </Button>

      <p className="font-default text-[10px] uppercase tracking-widest text-muted-foreground text-center">
        A CHAUFFEUR WILL CONFIRM WITHIN 24 HRS // SECURE BOOKING
      </p>
    </Form>;
}
export default function Reservation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [copied, setCopied] = React.useState<string>("");
  const handleCopy = (text: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(""), 1500);
    }
  };
  return;
}